import { FeatureName } from '../../graphql';
import { Feature } from '../domain';
import { useFeatures } from './useFeatures';
import { useCurrentWorkspace } from '../../auth/hooks/useCurrentWorkspace';
import { useCurrentAccount } from '../../auth/hooks/useCurrentAccount';

/**
 * Core feature check logic using a three-tier cascade:
 * 1. Global: If feature.enabled is true, the feature is available to everyone
 * 2. Workspace: If not globally enabled, checks if the user's current workspace is assigned to the feature
 * 3. Account: If not workspace-assigned, checks if the user's account is directly assigned to the feature
 *
 * @param feature - The feature to check
 * @param workspaceId - The current workspace ID
 * @param accountId - The current account ID
 * @returns true if the feature is enabled, false otherwise
 */
const checkFeatureEnabled = (
  feature: Feature | undefined,
  workspaceId: string | undefined,
  accountId: string | undefined
): boolean => {
  if (!feature) {
    return false;
  }

  // 1. If globally enabled, return true
  if (feature.enabled) {
    return true;
  }

  // 2. Check if assigned to current workspace
  if (workspaceId && feature.workspaces) {
    const isWorkspaceAssigned = feature.workspaces.some(
      (w) => w.id === workspaceId
    );
    if (isWorkspaceAssigned) {
      return true;
    }
  }

  // 3. Check if assigned to current account
  if (accountId && feature.accounts) {
    const isAccountAssigned = feature.accounts.some((a) => a.id === accountId);
    if (isAccountAssigned) {
      return true;
    }
  }

  return false;
};

/**
 * Checks if a feature is enabled for the current user using a three-tier cascade.
 *
 * This cascade allows for flexible feature rollout strategies:
 * - Full rollout: Set enabled: true
 * - Workspace-specific rollout: Assign to specific workspaces
 * - Individual testing: Assign to specific accounts
 *
 * @param featureName - The name of the feature to check
 * @returns true if the feature is enabled for the current user, false otherwise
 */
export const useIsFeatureEnabled = (featureName: FeatureName): boolean => {
  const { data } = useFeatures();
  const currentWorkspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();

  const features: Feature[] = data?.items ?? [];
  if (features.length === 0) {
    return false;
  }

  const feature = features.find((f) => f.name === featureName);
  return checkFeatureEnabled(feature, currentWorkspace?.id, account?.id);
};

/**
 * Returns a function that checks if a feature is enabled for the current user using a three-tier cascade.
 * This is useful when you need to check multiple features or check features conditionally.
 *
 * See useIsFeatureEnabled for documentation on the cascade logic.
 *
 * @returns A function that takes a featureName and returns true if enabled, false otherwise
 */
export const useIsFeatureEnabledFunc = () => {
  const { data } = useFeatures();
  const currentWorkspace = useCurrentWorkspace();
  const { account } = useCurrentAccount();

  const features: Feature[] = data?.items ?? [];

  const isFeatureEnabled = (featureName: FeatureName): boolean => {
    if (features.length === 0) {
      return false;
    }

    const feature = features.find((f) => f.name === featureName);
    return checkFeatureEnabled(feature, currentWorkspace?.id, account?.id);
  };

  return isFeatureEnabled;
};
